import { createClient } from '@supabase/supabase-js';
import CryptoJS from 'crypto-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const ENCRYPTION_KEY =
  import.meta.env.VITE_ENCRYPTION_KEY || 'your-fallback-key';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Encryption helpers
export const encrypt = (text: string) => {
  return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
};

export const decrypt = (ciphertext: string) => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
};

// Helper functions for authorization
export const getCurrentUser = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
};

export const isAdmin = async () => {
  const user = await getCurrentUser();
  if (!user) return false;

  const { data: profile } = await supabase
    .from('profiles')
    .select('is_admin')
    .eq('id', user.id)
    .single();

  return profile?.is_admin || false;
};

// Valentine page functions with authorization and encryption
export const createValentinePage = async (pageData: {
  sender: string;
  receiver: string;
  message: string;
  package_type: 'blush_of_love' | 'cupids_masterpiece';
  spotify_title: string;
  spotify_artist: string;
  card_message?: string;
  image1?: File;
  image2?: File;
  viewPassword: string;
}) => {
  const user = await getCurrentUser();
  if (!user)
    throw new Error(
      'Not authenticated. Please sign in to create a Valentine page.'
    );

  let image1Path = null;
  let image2Path = null;

  try {
    // Upload images if provided
    if (pageData.image1) {
      const { data: image1Data, error: image1Error } = await supabase.storage
        .from('valentine-images')
        .upload(`${user.id}/${Date.now()}-1`, pageData.image1);

      if (image1Error) {
        if (image1Error.message.includes('storage quota')) {
          throw new Error(
            'Image 1 is too large. Please use a smaller image (max 5MB).'
          );
        }
        throw new Error('Failed to upload Image 1. Please try again.');
      }
      image1Path = image1Data.path;
    }

    if (pageData.image2) {
      const { data: image2Data, error: image2Error } = await supabase.storage
        .from('valentine-images')
        .upload(`${user.id}/${Date.now()}-2`, pageData.image2);

      if (image2Error) {
        if (image2Error.message.includes('storage quota')) {
          throw new Error(
            'Image 2 is too large. Please use a smaller image (max 5MB).'
          );
        }
        throw new Error('Failed to upload Image 2. Please try again.');
      }
      image2Path = image2Data.path;
    }

    // Hash the view password
    const hashedPassword = CryptoJS.SHA256(pageData.viewPassword).toString();

    // Encrypt sensitive data
    const dbRecord = {
      user_id: user.id,
      sender: pageData.sender,
      receiver: encrypt(pageData.receiver),
      message: encrypt(pageData.message),
      package_type: pageData.package_type,
      spotify_title: pageData.spotify_title,
      spotify_artist: pageData.spotify_artist,
      card_message: pageData.card_message
        ? encrypt(pageData.card_message)
        : null,
      image1_path: image1Path,
      image2_path: image2Path,
      view_password: hashedPassword,
    };

    const { data, error } = await supabase
      .from('valentine_pages')
      .insert([dbRecord])
      .select()
      .single();

    if (error) {
      if (error.message.includes('rate limit exceeded')) {
        throw new Error(
          'You have created too many Valentine pages recently. Please try again in an hour.'
        );
      }
      if (error.message.includes('duplicate submission')) {
        throw new Error(
          'You recently created a similar Valentine. Please wait a few minutes before creating another.'
        );
      }
      throw new Error('Failed to create Valentine page. Please try again.');
    }

    return data;
  } catch (error) {
    // Clean up uploaded images if database insert fails
    if (image1Path) {
      await supabase.storage.from('valentine-images').remove([image1Path]);
    }
    if (image2Path) {
      await supabase.storage.from('valentine-images').remove([image2Path]);
    }
    throw error;
  }
};

export const getValentinePage = async (id: string, password: string) => {
  const hashedPassword = CryptoJS.SHA256(password).toString();

  const { data: page, error } = await supabase
    .from('valentine_pages')
    .select(
      `
      id,
      sender,
      receiver,
      message,
      package_type,
      spotify_title,
      spotify_artist,
      spotify_embed_url,
      image1_path,
      image2_path,
      card_message,
      is_approved,
      view_password
    `
    )
    .eq('id', id)
    .single();

  if (error) {
    if (error.message.includes('not found')) {
      throw new Error('This Valentine page does not exist.');
    }
    throw new Error('Unable to load Valentine page. Please try again.');
  }

  if (!page) throw new Error('Valentine not found');
  if (!page.is_approved)
    throw new Error(
      'This Valentine is still pending approval. Please check back later.'
    );
  if (hashedPassword !== page.view_password)
    throw new Error('Incorrect password. Please try again.');

  // Get image URLs if images exist
  let image1Url = null;
  let image2Url = null;

  if (page.image1_path) {
    const { data: image1Data } = await supabase.storage
      .from('valentine-images')
      .getPublicUrl(page.image1_path);
    image1Url = image1Data.publicUrl;
  }

  if (page.image2_path) {
    const { data: image2Data } = await supabase.storage
      .from('valentine-images')
      .getPublicUrl(page.image2_path);
    image2Url = image2Data.publicUrl;
  }

  // Decrypt sensitive data
  return {
    ...page,
    receiver: decrypt(page.receiver),
    message: decrypt(page.message),
    card_message: page.card_message ? decrypt(page.card_message) : null,
    image1_url: image1Url,
    image2_url: image2Url,
  };
};

export const getMyValentinePages = async () => {
  const user = await getCurrentUser();
  if (!user) throw new Error('Please sign in to view your Valentine pages.');

  const { data, error } = await supabase
    .from('valentine_pages')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error)
    throw new Error('Failed to load your Valentine pages. Please try again.');

  // Decrypt sensitive data for own pages
  return {
    data: data?.map((page) => ({
      ...page,
      receiver: decrypt(page.receiver),
      message: decrypt(page.message),
      card_message: page.card_message ? decrypt(page.card_message) : null,
    })),
  };
};

export const getAllValentinePages = async () => {
  if (!(await isAdmin())) {
    throw new Error('Unauthorized. Admin access required.');
  }

  const { data, error } = await supabase
    .from('valentine_pages')
    .select(
      `
      id,
      sender,
      package_type,
      spotify_title,
      spotify_artist,
      is_approved,
      created_at,
      profiles (
        username
      )
    `
    )
    .order('created_at', { ascending: false });

  if (error)
    throw new Error('Failed to load Valentine pages. Please try again.');
  return { data };
};

export const approveValentinePage = async (id: string, spotifyUrl: string) => {
  if (!(await isAdmin())) {
    throw new Error('Unauthorized. Admin access required.');
  }

  const { error } = await supabase
    .from('valentine_pages')
    .update({
      is_approved: true,
      spotify_embed_url: spotifyUrl,
    })
    .eq('id', id);

  if (error)
    throw new Error('Failed to approve Valentine page. Please try again.');
};
